import React, { useEffect, useState } from "react";
import clsx from "clsx";

import {
  checkIcon,
  copyIcon,
  plusFill,
  searchIcon,
  trashBin,
} from "@/components/icons/SVGIcons";
import EditableText from "@/lib/EditableText";
import { escapeRegExp } from "@/lib/utilities";
import { Button } from "@/components/UI/Button";
import { resetLogoPrices } from "@/components/UI/svg";
import { getFontSizeClass } from "@/components/widgets/tradeActionSummary/helper/actionSummaryHelper";
import useTableUIStore from "@/store/tableUIStore";
import priceTabStore, {
  IPriceConfigTab,
  isAtPriceConfigLimit,
  MAX_PRICE_CONFIGS,
  returnUserCreatedConfigs,
} from "@/store/pricesTabStore";
import {
  addNewPriceConfigToStoreAndSetAsActive,
  copyExistingConfigToStoreAndSetAsActive,
  removePriceConfigFromStoreAndDatabase,
} from "@/store/pricesTabStoreACTIONS";
import { useAuth0 } from "@auth0/auth0-react";
import { returnConfigListMinHeight } from "./helper/priceTableHelper";

export interface IPricesSidebarContent {
  activePricesConfigIdNumber: number;
  setActivePricesConfigIdNumber: Function;
  cryptoID?: string | undefined;
  api: any;
}

const ROW_ICON_BUTTON_CLASS =
  "flex h-6 w-6 shrink-0 items-center justify-center rounded opacity-70 hover:opacity-100 disabled:cursor-not-allowed disabled:opacity-30";

const PricesSidebarContent = ({
  setActivePricesConfigIdNumber,
  activePricesConfigIdNumber,
  cryptoID,
  api,
}: IPricesSidebarContent) => {
  const [inputValue, setInputValue] = useState<string>();
  const { getAccessTokenSilently } = useAuth0();

  const settingsConfigsFromStore = priceTabStore(
    (state) => state.priceConfigTabs,
  );
  const [settingsConfigToRender, setSettingsConfigToRender] = useState(
    settingsConfigsFromStore,
  );

  const currentActiveConfigDataToPassIdForRendering = priceTabStore(
    (state) => state.activePricesConfigIdNumberStoreArr,
  );

  const [
    // HOOK to render the active tab colour. The acitve tab ID for DB purposes is still kept on the price index.
    activePricesConfigIdNumberAtComponentHook,
    setActivePricesConfigIdNumberAtComponentHook,
  ] = useState<number>(activePricesConfigIdNumber);

  useEffect(() => {
    const CurrentActiveConfigID = // get the active tab ID out of the array.
      currentActiveConfigDataToPassIdForRendering.find(
        (config) => config.crypto == cryptoID,
      )?.activeTabId;
    if (CurrentActiveConfigID) {
      setActivePricesConfigIdNumberAtComponentHook(CurrentActiveConfigID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentActiveConfigDataToPassIdForRendering]);

  // -- Used to reduced the displayed config options based on user input.
  useEffect(() => {
    if (!inputValue || inputValue.length == 0) {
      setSettingsConfigToRender(settingsConfigsFromStore);
    } else {
      const escapedInput = escapeRegExp(inputValue);

      const regex = new RegExp(escapedInput, "i"); // 'i' flag for case-insensitive matching

      const valueToSet = settingsConfigsFromStore.filter((config) =>
        regex.test(config.displayName),
      );
      if (valueToSet) {
        setSettingsConfigToRender(valueToSet);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  useEffect(() => {
    // Update the render based on a change in Zustand state.
    const newMap = settingsConfigsFromStore.map((ele) => ele);
    setSettingsConfigToRender(newMap);
  }, [settingsConfigsFromStore]); // updates the list that will render for display names.

  // LQC-2575 - matches the other sidebars, which scale with the size setting.
  const fontSizeSetting = useTableUIStore((state) => state.fontSize);
  const fontSizeClass = getFontSizeClass(fontSizeSetting);

  // LQC-2575 - the same limit the store actions enforce, surfaced before the click.
  const userCreatedConfigCount = returnUserCreatedConfigs(
    settingsConfigsFromStore,
  ).length;
  const atConfigLimit = isAtPriceConfigLimit(settingsConfigsFromStore);
  const configLimitMessage = `Limit of ${MAX_PRICE_CONFIGS} views reached. Delete one to add another.`;

  // LQC-2909 - what the view list refuses to shrink below when the widget is short.
  const configListMinHeight = returnConfigListMinHeight(
    settingsConfigToRender.length,
  );

  const addNewConfigHandler = () => {
    addNewPriceConfigToStoreAndSetAsActive({
      setActivePricesConfigIdNumber,
      getAccessTokenSilently,
    }); // STORE ACTIONS
  };

  // Each row copies itself, where the old "Copy current" button copied the active view.
  const copyConfigHandler = (config: IPriceConfigTab) => {
    copyExistingConfigToStoreAndSetAsActive({
      activePricesConfigIdNumber: config.id,
      setActivePricesConfigIdNumber,
      getAccessTokenSilently,
    });
  };

  const changeActiveTab = (config: IPriceConfigTab) => {
    setActivePricesConfigIdNumberAtComponentHook(config.id);
    setActivePricesConfigIdNumber(config.id); // STORE ACTIONS
  };

  // STORE ACTIONS
  function removeConfigHandler(config: IPriceConfigTab) {
    removePriceConfigFromStoreAndDatabase({
      config,
      activePricesConfigIdNumber: activePricesConfigIdNumberAtComponentHook,
      setActivePricesConfigIdNumber,
      getAccessTokenSilently,
    });
  }

  return (
    <div
      data-testid={`PricesSidebarContainer-${cryptoID}`}
      id={`PricesSidebarContainer-${cryptoID}`}
      // min-w-0 lets the panel shrink with its tool-panel wrapper rather than
      // clipping, matching TradeSettingsSidebarContainer.
      className={clsx(
        "z-10 flex h-full w-full min-w-0 flex-col px-3 pb-2 pt-3",
        fontSizeClass,
      )}
    >
      <div className="mb-3 flex shrink-0 items-baseline justify-between">
        <div className="text-[1.15em] font-semibold text-gray-900 dark:text-white">
          Views
        </div>
        <div
          data-testid={`PricesSidebarConfigLimitMessage-${cryptoID}`}
          id={`PricesSidebarConfigLimitMessage-${cryptoID}`}
          title={atConfigLimit ? configLimitMessage : undefined}
          className={clsx(
            "text-[0.9em]",
            atConfigLimit
              ? "font-semibold text-orange-500"
              : "text-gray-500 dark:text-gray-400",
          )}
        >
          {userCreatedConfigCount} of {MAX_PRICE_CONFIGS}
        </div>
      </div>

      <div className="flex w-full min-h-0 flex-1 flex-col overflow-hidden">
        <div className="mb-3 flex shrink-0 items-center gap-2">
          <div className="flex h-[2.25em] min-w-0 flex-1 items-center gap-2 rounded border border-gray-300 bg-white px-2 text-gray-400 focus-within:border-sky-500 dark:border-gray-600 dark:bg-transparent dark:text-gray-500 dark:focus-within:border-[#5DA2D2]">
            {searchIcon}
            <input
              data-testid={`PricesSidebarSearchInput-${cryptoID}`}
              id={`PricesSidebarSearchInput-${cryptoID}`}
              type="text"
              placeholder="Search views..."
              className="h-full min-w-0 flex-1 bg-transparent text-gray-900 outline-none placeholder:text-gray-400 dark:text-white dark:placeholder:text-gray-500"
              onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
                setInputValue(e.target.value)
              }
            />
          </div>

          <Button
            data-testid={`PricesSidebarNewConfigButton-${cryptoID}`}
            id={`PricesSidebarNewConfigButton-${cryptoID}`}
            variant="mainOrangeAction"
            aria-label="New view"
            className="h-[2.25em] w-[2.25em] shrink-0 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={atConfigLimit}
            title={atConfigLimit ? configLimitMessage : "New view"}
            onClick={addNewConfigHandler}
          >
            {plusFill}
          </Button>
        </div>

        <div
          className="flex flex-1 flex-col gap-0.5 overflow-y-auto"
          style={{ minHeight: configListMinHeight }}
        >
          {settingsConfigToRender.map((config) => {
            const isActive =
              config.id == activePricesConfigIdNumberAtComponentHook;
            return (
              <div
                key={config.id}
                data-testid={`PricesSidebarViewRow-${config.id}`}
                id={`PricesSidebarViewRow-${config.id}`}
                className={clsx(
                  "flex min-h-[2.25em] w-full shrink-0 cursor-pointer items-center gap-1 overflow-hidden whitespace-nowrap rounded px-2",
                  isActive
                    ? "bg-[#BFDFF7] text-black dark:bg-[#3A6E9E] dark:text-white"
                    : "text-gray-800 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-white/5",
                )}
                onClick={() => changeActiveTab(config)}
              >
                <span className="flex w-4 shrink-0 items-center justify-center">
                  {isActive ? checkIcon : null}
                </span>
                <EditableText
                  initialText={config.displayName}
                  config={config}
                  updateStoreLocation={"pricesConfigs"}
                  getAccessTokenSilently={getAccessTokenSilently}
                  settingsConfigsFromStore={settingsConfigsFromStore}
                />
                <button
                  type="button"
                  data-testid={`PricesSidebarCopyConfigButton-${config.id}`}
                  id={`PricesSidebarCopyConfigButton-${config.id}`}
                  aria-label={`Duplicate ${config.displayName}`}
                  title={atConfigLimit ? configLimitMessage : "Duplicate view"}
                  disabled={atConfigLimit}
                  className={clsx(
                    ROW_ICON_BUTTON_CLASS,
                    "hover:bg-black/10 dark:hover:bg-white/10",
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    copyConfigHandler(config);
                  }}
                >
                  {copyIcon}
                </button>
                {config.id != -1 ? ( // -1 is the default
                  <button
                    type="button"
                    data-testid={`PricesSidebarDeleteConfigButton-${config.id}`}
                    id={`PricesSidebarDeleteConfigButton-${config.id}`}
                    aria-label={`Delete ${config.displayName}`}
                    title="Delete view"
                    className={clsx(
                      ROW_ICON_BUTTON_CLASS,
                      "hover:bg-red-500/15 hover:text-red-600 dark:hover:text-red-400",
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeConfigHandler(config);
                    }}
                  >
                    {trashBin}
                  </button>
                ) : (
                  <button
                    type="button"
                    data-testid={`PricesSidebarResetConfigButton-${config.id}`}
                    id={`PricesSidebarResetConfigButton-${config.id}`}
                    aria-label="Reset Default columns"
                    title="Reset columns"
                    className={clsx(
                      ROW_ICON_BUTTON_CLASS,
                      "hover:bg-black/10 hover:text-orange-500 dark:hover:bg-white/10",
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      api.resetColumnState();
                    }}
                  >
                    {resetLogoPrices}
                  </button>
                )}
              </div>
            );
          })}
          {settingsConfigToRender.length == 0 ? (
            <div
              data-testid={`PricesSidebarNoConfigsMessage-${cryptoID}`}
              id={`PricesSidebarNoConfigsMessage-${cryptoID}`}
              className="px-2 pt-4 text-gray-500 dark:text-gray-400"
            >
              No views match your search.
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default PricesSidebarContent;
